import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getNavConfig, peekNavConfig, PageConfigView } from '../api/Navigation/moduleConfig';
import { pageTabGroups } from '../components/layout/navigationModules';

type TabConfig = Record<string, boolean>;

interface TabConfigState {
  config: TabConfig;
  loaded: boolean;
}

const NOT_LOADED: TabConfigState = { config: {}, loaded: false };

const toTabConfig = (pages: PageConfigView[], groupId: string): TabConfig => {
  const config: TabConfig = {};
  pages
    .filter(pc => pc.moduleId === groupId)
    .forEach(pc => {
      config[pc.pagePath] = pc.isEnabled;
    });
  return config;
};

/**
 * Tab-level visibility for a page whose tabs an Operator can configure
 * (see `pageTabGroups` in navigationModules.ts).
 *
 * Tab config rides on the SAME per-company page-config storage as pages:
 * rows carry `moduleId = groupId` and `pagePath = tabId`.
 *
 * Behaviour (deliberately fail-open, matching the page/module filters):
 * - config still loading, or no rows for this group  => every tab is shown
 * - a tab with no row                                => shown
 * - a tab marked `alwaysEnabled` in the registry     => shown, always
 * Failing open means a config-fetch hiccup degrades to "too much UI", never to
 * an empty page.
 *
 * 🔴 FAILING OPEN WHILE LOADING IS VISIBLE, and callers must handle it. `isTabEnabled` answers
 * `true` for everything until `loaded` flips, so a caller that renders its strip regardless paints
 * the FULL tab list and then drops the disabled ones a tick later. Gate the strip on `loaded`.
 * (`UserProfile` did not, which is exactly what the flash on that page was.)
 *
 * 🔴 READS THE SHARED CACHE, and seeds from it SYNCHRONOUSLY. This used to call the raw, uncached
 * `getPageConfig()` from an effect on every mount. That is once per profile TAB SWITCH — each
 * `/profile/:id/<tab>` is a separate route rendering its own `<UserProfile>` wrapper, so the shell
 * unmounts and remounts on every tab — and it re-fetched a response `getNavConfig` already held,
 * having been resolved by `PageConfigGuard` before the route even rendered. Seeding from
 * `peekNavConfig` means a remount costs no request AND has no fail-open frame to correct: the
 * first paint is already the filtered one.
 */
export function usePageTabConfig(groupId: string) {
  const userId = useSelector((s: { user?: { userId?: string } }) => s.user?.userId);

  // Lazy initialiser, not an effect: when the shared config has already resolved this is the
  // first-paint state, so a remount never shows the unfiltered list at all.
  const [state, setState] = useState<TabConfigState>(() => {
    const cached = userId ? peekNavConfig(userId) : null;
    return cached ? { config: toTabConfig(cached.pages, groupId), loaded: true } : NOT_LOADED;
  });

  useEffect(() => {
    if (!userId) {
      // No user => nothing to scope a read to. Resolve as "no overrides", which fails open.
      setState({ config: {}, loaded: true });
      return;
    }

    const cached = peekNavConfig(userId);
    if (cached) {
      setState({ config: toTabConfig(cached.pages, groupId), loaded: true });
      return;
    }

    let cancelled = false;
    setState(NOT_LOADED);
    getNavConfig(userId)
      .then(({ pages }) => {
        if (cancelled) return;
        setState({ config: toTabConfig(pages, groupId), loaded: true });
      })
      .catch(() => {
        // getModuleConfig/getPageConfig already swallow their errors and resolve to []; this is
        // belt-and-braces so a rejection can never leave the strip permanently withheld.
        if (!cancelled) setState({ config: {}, loaded: true });
      });

    return () => {
      cancelled = true;
    };
  }, [groupId, userId]);

  const { config, loaded } = state;

  const isTabEnabled = useCallback(
    (tabId: string): boolean => {
      const group = pageTabGroups.find(g => g.id === groupId);
      if (group?.tabs.find(tb => tb.id === tabId)?.alwaysEnabled) return true;
      // Nothing loaded / no overrides for this group => show everything.
      if (!loaded || Object.keys(config).length === 0) return true;
      return config[tabId] ?? true;
    },
    [groupId, config, loaded]
  );

  return { isTabEnabled, loaded };
}
